import axios from 'axios'

// 创建axios实例
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8001',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 从localStorage获取token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    if (error.response?.status === 401) {
      // 清除本地存储的用户信息
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      // 重定向到登录页
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// API接口定义
export const authAPI = {
  // 企业微信登录
  wechatLogin: (code: string) => {
    return api.get(`/wechat/login?code=${code}`)
  },
  
  // 获取用户信息
  getUserInfo: (userId: string) => {
    return api.get(`/wechat/user/${userId}`)
  },
  
  // 获取当前用户信息
  getCurrentUser: () => {
    return api.get('/wechat/user')
  },
  
  // 获取用户列表
  getUserList: (params?: { skip?: number; limit?: number; is_active?: boolean }) => {
    return api.get('/wechat/users', { params })
  },
  
  // 更新用户状态
  updateUserStatus: (userId: string, isActive: boolean) => {
    return api.put(`/wechat/user/${userId}/status?is_active=${isActive}`)
  },
  
  // 健康检查
  healthCheck: () => {
    return api.get('/health')
  }
}

// 经销商API接口定义
export const dealerAPI = {
  // 搜索经销商
  searchDealers: (params: { keyword: string; page: number; limit: number }) => {
    return api.get('/dealers/search', { params })
  },
  
  // 更新销售顾问所属经销商（dealerId 为 UUID 字符串）
  updateConsultantDealer: (dealerId: number | string) => {
    const token = localStorage.getItem('token')
    return api.put(`/dealers/consultant/${dealerId}?token=${token}`)
  },
  
  // 获取经销商详情（dealerId 为 UUID 字符串）
  getDealerDetail: (dealerId: number | string) => {
    return api.get(`/dealers/${dealerId}`)
  },
  
  // 获取当前销售顾问的经销商
  getCurrentConsultantDealer: () => {
    const token = localStorage.getItem('token')
    return api.get(`/dealers/consultant/current?token=${token}`)
  }
}

// 车系API接口定义
export const carSeriesAPI = {
  // 获取所有启用的车系
  getAllCarSeries: () => {
    return api.get('/car-series/')
  },
  
  // 获取车系详情
  getCarSeriesDetail: (seriesId: number) => {
    return api.get(`/car-series/${seriesId}`)
  }
}

// 车型API接口定义
export const carModelAPI = {
  // 根据车系ID获取车型列表（seriesId 可为 UUID 字符串）
  getModelsBySeries: (seriesId: number | string) => {
    const token = localStorage.getItem('token')
    return api.get(`/car-models/series/${seriesId}?token=${token}`)
  },
  
  // 获取车型详情
  getModelDetail: (modelId: number) => {
    const token = localStorage.getItem('token')
    return api.get(`/car-models/${modelId}?token=${token}`)
  },
  
  // 获取所有车型
  getAllModels: () => {
    const token = localStorage.getItem('token')
    return api.get(`/car-models/?token=${token}`)
  }
}

// 交付标准API接口定义
export const deliveryStandardAPI = {
  // 根据车系ID获取交付标准列表（seriesId 可为 UUID 字符串）
  getStandardsBySeries: (seriesId: number | string) => {
    const token = localStorage.getItem('token')
    return api.get(`/delivery-standards/series/${seriesId}?token=${token}`)
  },
  
  // 获取交付标准详情
  getStandardDetail: (standardId: number) => {
    const token = localStorage.getItem('token')
    return api.get(`/delivery-standards/${standardId}?token=${token}`)
  },
  
  // 获取所有交付标准
  getAllStandards: () => {
    const token = localStorage.getItem('token')
    return api.get(`/delivery-standards/?token=${token}`)
  }
}

// 邀请函API接口定义
export const invitationAPI = {
  // 创建邀请函
  createInvitation: (params: {
    customerName: string;
    dealerId: number;
    seriesId: number;
    appointmentDate: string;
    appointmentHour: number;
    appointmentMinute: number;
    consultantPhone: string;
  }) => {
    const token = localStorage.getItem('token')
    return api.post('/invitations/create', {
      customer_name: params.customerName,
      dealer_id: params.dealerId,
      series_id: params.seriesId,
      appointment_date: params.appointmentDate,
      appointment_hour: params.appointmentHour,
      appointment_minute: params.appointmentMinute,
      consultant_phone: params.consultantPhone
    }, {
      params: {
        token
      }
    })
  },
  
  // 获取邀请函列表
  getInvitationsList: (limit: number = 10) => {
    const token = localStorage.getItem('token')
    return api.get(`/invitations/list?limit=${limit}&token=${token}`)
  },
  
  // 发送企业微信消息
  sendWechatMessage: (invitationId: string) => {
    const token = localStorage.getItem('token')
    return api.post(`/invitations/send-wechat-message/${invitationId}?token=${token}`)
  },
  
  // 获取邀请函链接
  getInvitationLink: (invitationId: string) => {
    const token = localStorage.getItem('token')
    return api.get(`/invitations/link/${invitationId}?token=${token}`)
  },
  
  // 获取邀请函详情
  getInvitationDetail: (invitationId: string) => {
    const token = localStorage.getItem('token')
    return api.get(`/invitations/${invitationId}?token=${token}`)
  }
}

// 交车邀请函API接口定义
export const deliveryReservationAPI = {
  // 创建交车邀请函
  createDeliveryReservation: (params: {
    dealerId: number;
    seriesId: number;
    customerName: string;
    vehicleModel?: string;
    vehicleColor?: string;
    vehicleQuantity?: number;
    deliveryStandardId?: number;
    consultantPhone?: string;
    appointmentDate?: string;
    appointmentHour?: number;
    appointmentMinute?: number;
  }) => {
    const token = localStorage.getItem('token')
    return api.post('/delivery-reservations/create', {
      dealer_id: params.dealerId,
      series_id: params.seriesId,
      customer_name: params.customerName,
      vehicle_model: params.vehicleModel,
      vehicle_color: params.vehicleColor,
      vehicle_quantity: params.vehicleQuantity,
      delivery_standard_id: params.deliveryStandardId,
      consultant_phone: params.consultantPhone,
      appointment_date: params.appointmentDate,
      appointment_hour: params.appointmentHour,
      appointment_minute: params.appointmentMinute
    }, {
      params: {
        token
      }
    })
  },
  
  // 获取交车邀请函列表
  getDeliveryReservationsList: (limit: number = 10) => {
    const token = localStorage.getItem('token')
    return api.get(`/delivery-reservations/list?limit=${limit}&token=${token}`)
  },
  
  // 获取交车邀请函详情
  getDeliveryReservationDetail: (reservationId: string) => {
    const token = localStorage.getItem('token')
    return api.get(`/delivery-reservations/${reservationId}?token=${token}`)
  },
  
  // 发送企业微信消息
  sendWechatMessage: (reservationId: string) => {
    const token = localStorage.getItem('token')
    return api.post(`/delivery-reservations/send-wechat-message/${reservationId}?token=${token}`)
  },
  
  // 获取交车邀请函链接
  getDeliveryReservationLink: (reservationId: string) => {
    const token = localStorage.getItem('token')
    return api.get(`/delivery-reservations/link/${reservationId}?token=${token}`)
  }
}

// 回执查看API接口定义
export const receiptAPI = {
  // 获取回执列表
  getReceiptsList: (params: {
    receiptStatus?: number | null;
    invitationType?: string | null;
    timeSort?: string;
    limit?: number;
    page?: number;
  }) => {
    const token = localStorage.getItem('token')
    return api.get('/receipts/list', {
      params: {
        token,
        receipt_status: params.receiptStatus,
        invitation_type: params.invitationType,
        time_sort: params.timeSort,
        limit: params.limit,
        page: params.page
      }
    })
  },
  
  // 获取回执统计信息
  getReceiptsStats: () => {
    const token = localStorage.getItem('token')
    return api.get(`/receipts/stats?token=${token}`)
  },
  
  // 获取回执详情
  getReceiptDetail: (receiptType: string, receiptId: string) => {
    const token = localStorage.getItem('token')
    return api.get(`/receipts/${receiptType}/${receiptId}?token=${token}`)
  },
  
  // 获取筛选选项
  getFilterOptions: () => {
    const token = localStorage.getItem('token')
    return api.get(`/receipts/filters/options?token=${token}`)
  },

  // 重发消息
  resendMessage: (receiptId: string, receiptType: string) => {
    const token = localStorage.getItem('token')
    return api.post(`/receipts/${receiptId}/resend?token=${token}`, {
      receipt_type: receiptType
    })
  }
}

// 用户管理API接口定义
export const userAPI = {
  // 更新用户资料
  updateProfile: (data: {
    consultant_name?: string
    consultant_phone?: string
    is_sync_wecom?: boolean
    dealer_id?: number
  }) => {
    return api.put('/wechat/user/profile', data)
  },

  // 上传头像
  uploadAvatar: (formData: FormData) => {
    const token = localStorage.getItem('token')
    console.log('上传头像，token:', token ? '存在' : '不存在')
    if (!token) {
      throw new Error('未找到认证token')
    }
    return api.post(`/wechat/user/avatar?token=${token}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  // 获取用户信息
  getUserInfo: (userId: string) => {
    return api.get(`/wechat/user/${userId}`)
  },

  // 获取当前用户信息
  getCurrentUser: () => {
    return api.get('/wechat/user')
  },

  // 更新企业微信同步状态
  updateSyncWecom: (userId: string, isSyncWecom: boolean) => {
    return api.put(`/wechat/user/${userId}/sync-wecom`, { is_sync_wecom: isSyncWecom })
  }
}

export default api
